import React from 'react';
import { Link } from 'react-router-dom';

export default function FathomTerms() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-lg font-mono text-zinc-400 hover:text-white transition-colors">
            ← Back to Portfolio
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Use</h1>
            <p className="text-zinc-400 text-lg">Fathom - Scuba Dive Logbook</p>
            <p className="text-zinc-500 text-sm mt-2">Last updated: July 21, 2026</p>
          </div>

          <div className="space-y-10 text-zinc-300 leading-relaxed">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Agreement</h2>
              <p>
                By downloading or using Fathom ("the App"), you agree to these Terms of Use. If
                you do not agree, please do not use the App. The App is distributed through the
                Apple App Store, and your download and any purchases are also subject to
                Apple's terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Not a Dive Planning or Safety Tool</h2>
              <p>
                Fathom is a logbook. It records dives after they happen. It is not a dive
                computer, decompression software, gas planner, or training tool, and it must
                never be used to plan dives, calculate no-decompression limits, or make any
                safety decision in or around the water. Always dive within the limits of your
                training and certification, follow your dive computer and your training
                agency's guidelines, and when anything in the App disagrees with your dive
                computer or your instructor, trust them, not us. We accept no responsibility
                for diving decisions made using information stored in or displayed by the App.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">License</h2>
              <p>
                We grant you a personal, non-exclusive, non-transferable license to use the App
                on devices you own or control, for your personal, non-commercial use. You may
                not copy, modify, distribute, sell, or reverse-engineer the App except where
                permitted by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Your Data</h2>
              <p>
                All content you create in the App (your dives, sites, species sightings,
                buddies, certifications, notes, photos, and collected signatures) belongs to
                you and is stored on your device. You are responsible for backing it up using
                the App's export feature. Deleting the App deletes its local data, and we have
                no way to recover it for you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Signatures &amp; Logbook Records</h2>
              <p>
                The instructor sign-off feature stores a drawn signature with a name, agency,
                and date on a dive entry. It is a convenience for record-keeping, equivalent to
                a signature in a paper logbook. Whether any organization, dive shop, or
                training agency accepts digitally signed logs is at their discretion, and we
                make no guarantee of acceptance. You are responsible for the accuracy of your
                log entries and for obtaining signatures honestly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Fathom Premium</h2>
              <p>
                Fathom Premium is a one-time, non-refundable in-app purchase processed by
                Apple (refunds are handled by Apple under their policies). It unlocks
                additional features on any device signed into the same Apple ID, using the
                App's restore function. Premium is a license to features, not a service: there
                is no subscription, and nothing about your existing data is ever conditional
                on purchasing it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Reference Data &amp; Trademarks</h2>
              <p>
                The App includes a catalog of factual reference information (well-known dive
                site names and locations, and common marine species names). This information
                is provided for convenience and may contain inaccuracies. PADI, SSI, NAUI,
                RAID, CMAS, and other agency names are trademarks of their respective
                organizations. Fathom is an independent app and is not affiliated with,
                endorsed by, or sponsored by any diving agency or organization.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Disclaimer of Warranties</h2>
              <p>
                The App is provided "as is" and "as available," without warranties of any
                kind, express or implied, including fitness for a particular purpose and
                non-infringement. We do not warrant that the App will be uninterrupted or
                error-free.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, we shall not be liable for any
                indirect, incidental, special, consequential, or exemplary damages arising
                from your use of the App, including loss of data. Our total liability for any
                claim relating to the App is limited to the amount you paid for it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Changes</h2>
              <p>
                We may update the App and these Terms from time to time. Material changes will
                be reflected by revising the "Last updated" date above. Continued use of the
                App after changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Contact</h2>
              <p>
                Questions about these Terms? Contact us at{' '}
                <a
                  href="mailto:parkerdevwork@gmail.com"
                  className="text-cyan-400 hover:text-cyan-300 underline"
                >
                  parkerdevwork@gmail.com
                </a>. Our{' '}
                <Link
                  to="/fathom/privacy-policy"
                  className="text-cyan-400 hover:text-cyan-300 underline"
                >
                  Privacy Policy
                </Link>{' '}
                explains how your data is handled.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center text-sm text-zinc-600">
          <p>© 2026 Fathom. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
